import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TENDER_METHODS } from "@/lib/constants";
import { fmtMoney } from "@/lib/format";
import { paymentLinesTotal } from "@/lib/saleMath";
import { toast } from "sonner";

const blankLine = { method: "cash", amount: "", reference: "", surcharge_pct: "", platform_id: "" };

// Stable client-side id for React keys. crypto.randomUUID is widely supported;
// fall back to a Math.random key for older Safari just in case.
function newLineId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `pl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// Compute card surcharge in rupees from base amount + pct.
function calcSurcharge(amount, pct) {
  const a = Number(amount) || 0;
  const p = Number(pct) || 0;
  return Math.round(a * p) / 100;
}

export default function PaymentLinesEditor({
  value = [],
  onChange,
  methods = TENDER_METHODS,
  label = "Payment Mix",
}) {
  const [draft, setDraft] = useState({ ...blankLine, method: methods[0] || "cash" });
  const [platforms, setPlatforms] = useState([]);

  // Load the platform master once — only needed when the "platform" method is offered.
  useEffect(() => {
    if (!methods.includes("platform")) return;
    let cancelled = false;
    api
      .get("/platforms", { params: { active_only: true } })
      .then((r) => {
        if (!cancelled) setPlatforms(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setPlatforms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [methods]);

  const refRequired = draft.method !== "cash";
  const isCard = draft.method === "card";
  const isPlatform = draft.method === "platform";
  const draftSurcharge = isCard ? calcSurcharge(draft.amount, draft.surcharge_pct) : 0;
  const draftTotalSwiped = (Number(draft.amount) || 0) + draftSurcharge;

  const add = () => {
    if (!draft.amount || Number(draft.amount) <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    // Platform check FIRST so a missing platform doesn't get masked by the
    // generic "reference required" message that fires for every non-cash line.
    if (isPlatform && !draft.platform_id) {
      toast.error("Pick the booking platform (TVS Direct, Flipkart, etc.).");
      return;
    }
    const ref = (draft.reference || "").trim();
    if (refRequired && !ref) {
      toast.error(`${draft.method.toUpperCase()} payment needs a reference (UTR / txn id).`);
      return;
    }
    if (ref) {
      // Local guard against same reference twice in the form. Backend enforces
      // global uniqueness across all sales as well.
      const dup = value.some(
        (l) => (l.reference || "").trim().toLowerCase() === ref.toLowerCase(),
      );
      if (dup) {
        toast.error(`Reference '${ref}' is already used in another payment line.`);
        return;
      }
    }
    const pct = isCard ? Number(draft.surcharge_pct) || 0 : 0;
    if (pct < 0 || pct > 10) {
      toast.error("Surcharge % must be between 0 and 10.");
      return;
    }
    const platform = isPlatform ? platforms.find((p) => p.id === draft.platform_id) : null;
    onChange([
      ...value,
      {
        _id: newLineId(),
        method: draft.method,
        amount: Number(draft.amount),
        reference: ref,
        surcharge_pct: pct,
        surcharge_amount: calcSurcharge(draft.amount, pct),
        ...(isPlatform
          ? { platform_id: draft.platform_id, platform_name: platform?.name || "" }
          : {}),
      },
    ]);
    setDraft({ ...blankLine, method: methods[0] || "cash" });
  };
  const remove = (id) => onChange(value.filter((l) => l._id !== id));

  const total = paymentLinesTotal(value);

  return (
    <div data-testid="payment-lines-editor">
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        {label} · {fmtMoney(total)}
      </label>
      <div className="border border-canvas-line">
        {value.length === 0 ? (
          <div className="px-3 py-3 font-mono text-[11px] text-ink-subtle italic">
            No tenders captured. Add at least one payment line below.
          </div>
        ) : (
          <ul>
            {value.map((line) => (
              <li
                key={line._id || `${line.method}-${line.amount}-${line.reference}`}
                data-testid={`payment-line-${line._id || "legacy"}`}
                className="flex items-center justify-between gap-2 px-3 py-2 border-b border-canvas-line last:border-b-0 text-xs"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-canvas-line">
                    {line.method}
                  </span>
                  <span className="font-mono font-semibold text-ink">
                    {fmtMoney(line.amount)}
                  </span>
                  {line.method === "card" && Number(line.surcharge_amount) > 0 && (
                    <span
                      className="font-mono text-[10px] text-ink-muted"
                      title={`Customer swiped ${fmtMoney(Number(line.amount) + Number(line.surcharge_amount))} (incl. ${line.surcharge_pct}% surcharge)`}
                      data-testid={`payment-line-surcharge-${line._id || "legacy"}`}
                    >
                      +{fmtMoney(line.surcharge_amount)} ({line.surcharge_pct}%)
                    </span>
                  )}
                  {line.method === "platform" && line.platform_name && (
                    <span
                      className="font-mono text-[10px] text-[#002FA7]"
                      data-testid={`payment-line-platform-${line._id || "legacy"}`}
                    >
                      via {line.platform_name}
                    </span>
                  )}
                  {line.reference && (
                    <span className="text-ink-subtle truncate">· {line.reference}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(line._id)}
                  data-testid={`payment-line-remove-${line._id || "legacy"}`}
                  className="text-ink-subtle hover:text-destructive"
                  aria-label="Remove"
                >
                  <Trash size={11} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-dashed border-canvas-line p-3 grid grid-cols-12 gap-2 items-end mt-2">
        <div className="col-span-3">
          <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Method
          </label>
          <select
            value={draft.method}
            onChange={(e) => setDraft({ ...draft, method: e.target.value })}
            data-testid="payment-line-draft-method"
            className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
          >
            {methods.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-3">
          <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Amount (₹)
          </label>
          <input
            type="number"
            value={draft.amount}
            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            data-testid="payment-line-draft-amount"
            className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
          />
        </div>
        <div className="col-span-4">
          <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Reference / Note
          </label>
          <input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            placeholder="optional"
            data-testid="payment-line-draft-ref"
            className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
          />
        </div>
        <div className="col-span-2">
          <button
            type="button"
            onClick={add}
            data-testid="payment-line-add"
            className="w-full px-3 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand transition-colors flex items-center justify-center gap-1"
          >
            <Plus size={11} weight="bold" /> Add
          </button>
        </div>
        {isCard && (
          <div className="col-span-12 grid grid-cols-12 gap-2 items-end border-t border-canvas-line pt-2">
            <div className="col-span-3">
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Card Surcharge %
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={draft.surcharge_pct}
                onChange={(e) => setDraft({ ...draft, surcharge_pct: e.target.value })}
                placeholder="e.g., 1"
                data-testid="payment-line-draft-surcharge-pct"
                className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
              />
            </div>
            <div className="col-span-9 font-mono text-[11px] text-ink-muted">
              {Number(draft.surcharge_pct) > 0 ? (
                <span data-testid="payment-line-draft-swiped">
                  Customer swipes <strong className="text-ink">{fmtMoney(draftTotalSwiped)}</strong>
                  {" "}= {fmtMoney(draft.amount || 0)} + {fmtMoney(draftSurcharge)} surcharge
                </span>
              ) : (
                <span className="text-ink-subtle italic">
                  Add a % to pass the bank swipe fee to the customer. Receipt prints the swiped total only.
                </span>
              )}
            </div>
          </div>
        )}
        {isPlatform && (
          <div className="col-span-12 grid grid-cols-12 gap-2 items-end border-t border-canvas-line pt-2">
            <div className="col-span-5">
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Booking Platform <span className="text-destructive">*</span>
              </label>
              <select
                value={draft.platform_id}
                onChange={(e) => setDraft({ ...draft, platform_id: e.target.value })}
                data-testid="payment-line-draft-platform"
                className="w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink"
              >
                <option value="">— Pick a platform —</option>
                {platforms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {platforms.length === 0 && (
                <p className="font-mono text-[10px] text-destructive mt-1">
                  No platforms configured. Admin → Settings → Platforms.
                </p>
              )}
            </div>
            <div className="col-span-7 font-mono text-[11px] text-ink-muted italic">
              Customer paid the platform directly · the platform will settle this amount with us later (tracked in Accounts → Platform Receivables).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
